import { useState, useRef, useMemo, createContext } from 'react';

// ----------------------------------------------------------------------

const initialState = {
  modalQueue: {},
  modalMeta: {},
};

const ModalContext = createContext(initialState);

// Stable callbacks only. Components that just trigger modals (e.g. every property
// card) read this context so they do not re-render each time a modal opens or closes.
const ModalActionsContext = createContext({});

function ModalProvider({ children }) {

  const [modalQueue, setModalQueue] = useState(initialState.modalQueue);
  const [modalMeta, setModalMeta] = useState(initialState.modalMeta);

  // Mirror of the latest state so stable callbacks can read it without re-subscribing.
  const queueRef = useRef(modalQueue);
  queueRef.current = modalQueue;

  const actions = useMemo(() => ({
    addToQueue: ({ reference = '', isOpen = false, meta = {} }) => {
      setModalQueue(previous => Object.prototype.hasOwnProperty.call(previous, reference)
        ? previous
        : { ...previous, [reference]: isOpen });
      setModalMeta(previous => Object.prototype.hasOwnProperty.call(previous, reference)
        ? previous
        : { ...previous, [reference]: meta });
    },
    closeModal: (reference = '') => {
      setModalQueue(previous => ({ ...previous, [reference]: false }));
    },
    openModal: (reference = '') => {
      setModalQueue(previous => ({ ...previous, [reference]: true }));
    },
    toggleModal: (reference = '') => {
      setModalQueue(previous => ({ ...previous, [reference]: !previous[reference] }));
    },
    updateModalMeta: (reference = '', meta = {}) => {
      setModalMeta(previous => ({
        ...previous,
        [reference]: { ...previous[reference], ...meta },
      }));
    },
    isModalOpen: (reference = '') => !!queueRef.current[reference],
  }), []);

  const isOpen = (reference = '') => {
    return modalQueue[reference] ? modalQueue[reference] : false;
  }

  return (
    <ModalActionsContext.Provider value={actions}>
      <ModalContext.Provider
        value={{
          addToQueue: actions.addToQueue,
          isOpen,
          closeModal: actions.closeModal,
          openModal: actions.openModal,
          toggleModal: actions.toggleModal,
          updateModalMeta: actions.updateModalMeta,
          modalMeta,
          modalQueue,
        }}
      >
        {children}
      </ModalContext.Provider>
    </ModalActionsContext.Provider>
  );
}

export { ModalProvider, ModalContext, ModalActionsContext };
